import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Chip, CircularProgress, TextField, InputAdornment, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, MenuItem, IconButton,
  Tooltip, Alert, Tabs, Tab, Switch, FormControlLabel
} from '@mui/material';
import { Add, Search, Refresh, Edit, Delete, Security, Warning } from '@mui/icons-material';
import api from '../../services/api';

const emptyRule = { name: '', rule_type: 'daily_spend', threshold: '', action: 'alert', active: true };

export const FraudPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [rules, setRules] = useState<any[]>([]);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState<any>(emptyRule);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/fraud/rules');
      setRules(Array.isArray(data) ? data : (data.data || []));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/fraud/alerts?limit=50');
      setAlerts(data.data || (Array.isArray(data) ? data : []));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (tab === 0) loadRules(); else loadAlerts(); }, [tab, loadRules, loadAlerts]);

  const save = async () => {
    setSaving(true); setError('');
    try {
      const payload = { ...form, threshold: parseFloat(form.threshold) || 0 };
      if (editing) await api.put(`/fraud/rules/${editing.id}`, payload);
      else await api.post('/fraud/rules', payload);
      setDialog(false); loadRules();
    } catch (e: any) { setError(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const acknowledge = async (id: string) => {
    await api.patch(`/fraud/alerts/${id}/acknowledge`); loadAlerts();
  };

  const RULE_TYPES = [
    { value: 'daily_spend', label: 'Daily Spend Limit ($)' },
    { value: 'hourly_spend', label: 'Hourly Spend Limit ($)' },
    { value: 'concurrent_calls', label: 'Concurrent Calls limit' },
    { value: 'cps_limit', label: 'Calls Per Second (CPS) limit' },
    { value: 'asr_threshold', label: 'ASR Threshold (%)' },
    { value: 'destination_block', label: 'Destination Block' },
    { value: 'country_block', label: 'Country Block' },
    { value: 'international_block', label: 'International Block' },
  ];
  const ACTIONS = [
    { value: 'alert', label: 'Alert only' },
    { value: 'block', label: 'Block call' },
    { value: 'suspend', label: 'Suspend account' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Security sx={{ color: 'error.main', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Fraud Protection</Typography>
            <Typography variant="body2" color="text.secondary">Rules and alerts for fraud detection</Typography>
          </Box>
        </Box>
        {tab === 0 && (
          <Button variant="contained" startIcon={<Add />} color="error"
            onClick={() => { setEditing(null); setForm({ ...emptyRule }); setError(''); setDialog(true); }}>
            New Rule
          </Button>
        )}
        {tab === 1 && (
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadAlerts}>Refresh</Button>
        )}
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Rules (${rules.length})`} />
        <Tab label={`Alerts (${alerts.length})`} icon={alerts.filter(a => !a.acknowledged_at).length > 0 ? <Warning color="error" fontSize="small" /> : undefined} iconPosition="end" />
      </Tabs>

      <Card>
        {loading ? <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box> : tab === 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Rule Name', 'Type', 'Threshold', 'Action', 'Active', 'Actions'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map(row => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                  <TableCell><Chip label={RULE_TYPES.find(t => t.value === row.rule_type)?.label || row.rule_type} size="small" variant="outlined" /></TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{row.threshold ?? '—'}</TableCell>
                  <TableCell><Chip label={row.action} size="small" color={row.action === 'block' ? 'error' : row.action === 'suspend' ? 'warning' : 'info'} /></TableCell>
                  <TableCell><Chip label={row.active ? 'Active' : 'Inactive'} size="small" color={row.active ? 'success' : 'default'} /></TableCell>
                  <TableCell>
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(row); setForm({ name: row.name, rule_type: row.rule_type, threshold: String(row.threshold || ''), action: row.action, active: row.active }); setError(''); setDialog(true); }}><Edit fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={async () => { if (window.confirm('Delete this fraud rule?')) { await api.delete(`/fraud/rules/${row.id}`); loadRules(); } }}><Delete fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {rules.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>No fraud rules. Create rules to protect against abuse.</TableCell></TableRow>}
            </TableBody>
          </Table>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Alert', 'Rule', 'Severity', 'Created', 'Status', 'Actions'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {alerts.map(row => (
                <TableRow key={row.id} hover sx={!row.acknowledged_at ? { bgcolor: 'error.lighter' } : {}}>
                  <TableCell sx={{ fontWeight: 600 }}>{row.alert_type || row.type || '—'}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{rules.find(r => r.id === row.rule_id)?.name || '—'}</TableCell>
                  <TableCell><Chip label={row.severity || 'medium'} size="small" color={row.severity === 'critical' ? 'error' : row.severity === 'high' ? 'warning' : 'info'} /></TableCell>
                  <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</TableCell>
                  <TableCell><Chip label={row.acknowledged_at ? 'Acknowledged' : 'Open'} size="small" color={row.acknowledged_at ? 'default' : 'error'} /></TableCell>
                  <TableCell>
                    {!row.acknowledged_at && <Button size="small" onClick={() => acknowledge(row.id)}>Acknowledge</Button>}
                  </TableCell>
                </TableRow>
              ))}
              {alerts.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>No fraud alerts. System is clean.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Fraud Rule' : 'New Fraud Rule'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Rule Name *" value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })} required />
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth label="Rule Type *" value={form.rule_type}
                onChange={e => setForm({ ...form, rule_type: e.target.value })}>
                {RULE_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Threshold" value={form.threshold}
                onChange={e => setForm({ ...form, threshold: e.target.value })}
                helperText="Limit value that triggers the rule" />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Action" value={form.action}
                onChange={e => setForm({ ...form, action: e.target.value })}>
                {ACTIONS.map(a => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} />} label="Rule Active" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={save} disabled={saving || !form.name}>
            {saving ? <CircularProgress size={20} /> : editing ? 'Save Changes' : 'Create Rule'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
